import { REGEX } from "../constants/regex";
import { ResidentialForm } from "../hero_component/ResidentialForm";
import { getCountryCode } from "../location-search-map/getCountryCode";
import { startLoader, stopLoader } from "../util/loader"
import { getFranchiseDetails, getBrandDetailsFromBrandJson } from "../util/share";
import { StateHandler } from "../util/getStateFun";

interface FormFields {
  address1: string;
  address2: string;
  city: string;
  state: string;    
  zipCode: string;
}

export class LocateAddressBumper {
  private sendButton: HTMLButtonElement | null;
  private formElement: HTMLFormElement;
  constructor() {
    const modalContent = document.querySelector('.find-local-modal .modal-loc-div');
    this.formElement = document.querySelector('.step-2 .modal-search') as HTMLFormElement;
    if (modalContent) {
      modalContent.classList.add('banner-wizard');
    }
    this.sendButton = document.querySelector('.step-2 .form-cta button');
    if (this.sendButton) {
      this.sendButton.addEventListener("click", this.handleNextBtnClick.bind(this));
    }

    const closeButton = document.getElementById('close-local-modal');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.handleModalClose();
      });
    }
    this.setupInlineValidation();
  }

  private validateField(element: HTMLInputElement | HTMLSelectElement, validationFunction: () => string): void {
    const error = validationFunction();
    const errorElement = element.nextElementSibling as HTMLSpanElement;
    if (error) {
      element.classList.add('invalid-field');
      if (errorElement?.classList.contains('error-msg')) {
        errorElement.textContent = error;
      } else {
        const newErrorElement = document.createElement('span');
        newErrorElement.className = 'error-msg';
        newErrorElement.textContent = error;
        element.parentNode?.insertBefore(newErrorElement, element.nextSibling);
      }
    } else {
      element.classList.remove('invalid-field');
      if (errorElement?.classList.contains('error-msg')) {
        errorElement.remove();
      }
    }
  }

  private setupInlineValidation(): void {
    const fields = this.getFormFieldsElement();
    if (!fields) return;

    // Address 1 validation
    if (fields?.address1) {
      fields.address1.addEventListener('input', (e) => 
        this.validateField(e.target as HTMLInputElement, () => 
          this.validateAddress(fields.address1?.value ?? '', true)
        )
      );
    }

    // Address 2 validation
    if (fields?.address2) {
      fields.address2.addEventListener('input', (e) => 
        this.validateField(e.target as HTMLInputElement, () => 
          this.validateAddress(fields.address2?.value ?? '', false)
        )
      );
    }

    // City validation
    if (fields?.city) {
      fields.city.addEventListener('input', (e) => 
        this.validateField(e.target as HTMLInputElement, () => 
          this.validateCity(fields.city?.value ?? '')
        )
      );
    }

    // Zip code validation
    if (fields?.zipCode) {
      fields.zipCode.addEventListener('input', (e) => {
        const inputVal = (e.target as HTMLInputElement)?.value;
        if (inputVal.trim().length >= 5) {
          sessionStorage.setItem('temp_zipcode', inputVal);
          const stateHandler = StateHandler.getInstance();
          stateHandler.getStateFun();
        }
        this.validateField(e.target as HTMLInputElement, () => 
          this.zipValidation(inputVal) ? '' : this.validateZipCode(inputVal)
        );
      });
    }

    // State validation
    if (fields?.state) {
      fields.state.addEventListener('change', (e) => 
        this.validateField(e.target as HTMLSelectElement, () => 
          this.validateState(fields.state?.value ?? '')
        )
      );
    }
  }

  private getFormFieldsElement(): {
    address1: HTMLInputElement | null;
    address2: HTMLInputElement | null;
    city: HTMLInputElement | null;
    state: HTMLSelectElement | null;
    zipCode: HTMLInputElement | null;
  } {
    return {
      address1: document.getElementById('modal-address-1') as HTMLInputElement,
      address2: document.getElementById('modal-address-2') as HTMLInputElement,
      city: document.getElementById('modal-city') as HTMLInputElement,
      state: document.getElementById('modal-states') as HTMLSelectElement,
      zipCode: document.getElementById('modal-zipcode') as HTMLInputElement
    };
  }

  private getFormFields(): FormFields {
    const elements = this.getFormFieldsElement();
    return {
      zipCode: elements.zipCode?.value.trim() ?? '',
      address1: elements.address1?.value.trim() ?? '',
      address2: elements.address2?.value.trim() ?? '',
      city: elements.city?.value.trim() ?? '',
      state: elements.state?.value.trim() ?? ''
    };
  }

  private validateAddress(value: string, isRequired: boolean): string {
    if (isRequired && !value.trim()) {
      return 'Address Line 1 is required.';
    }
    return value.trim() && !REGEX.noBracketAddress.test(value) ? 'Enter valid address.' : '';
  }

  private validateZipCode(value: string): string {
    if (!value.trim()) {
      return 'Zip code is required.';
    }
    return !REGEX.sendZip.test(value.trim()) || value.trim() === '00000' ? 'Invalid zip code format.' : '';
  }

  private zipValidation(value: string): boolean {
    if (!value.trim()) {
      return false;
    }
    return REGEX.sendZip.test(value.trim()) && value.trim() !== '00000';
  }
  
  private validateCity(value: string): string {
    if (!value.trim()) {
      return 'City is required.';
    }
    return !REGEX.sendName.test(value) ? 'Enter valid city.' : '';
  }

  private validateState(value: string): string {
    return !value.trim() ? 'State is required.' : '';
  }

  public validateForm(): boolean {
    const formFields = this.getFormFieldsElement();
    this.clearValidationErrors();

    const validationResults = [
      { id: 'modal-address-1', value: formFields.address1?.value ?? '', validator: (v: string) => this.validateAddress(v, true) },
      { id: 'modal-address-2', value: formFields.address2?.value ?? '', validator: (v: string) => this.validateAddress(v, false) },
      { id: 'modal-city', value: formFields.city?.value ?? '', validator: this.validateCity },
      { id: 'modal-zipcode', value: formFields.zipCode?.value ?? '', validator: this.validateZipCode },
      { id: 'modal-states', value: formFields.state?.value ?? '', validator: this.validateState }
    ].map(({ id, value, validator }) => ({
      id,
      value,
      error: validator(value)
    }));

    const errors = validationResults.filter(result => result.error);
    errors.forEach(({ id, error }) => {
      const field = document.getElementById(id);
      if (field && error) {
        const errorElement = document.createElement('span');
        errorElement.className = 'error-msg';
        errorElement.textContent = error;
        field.classList.add('invalid-field');
        field.parentNode?.appendChild(errorElement);
      }
    });

    if (errors.length > 0) {
      const firstErrorField = document.getElementById(errors[0].id);
      firstErrorField?.focus();
      return false;
    }

    return true;
  }

  public handleNextBtnClick(event: Event): void {
    event.preventDefault();
    const isValid = this.validateForm();
    if (isValid) {
      startLoader();
      this.handleNextBtnSubmit(event);
    }
  }

  public handleAddressBumperModalClose() {
    const closeButton = document.getElementById('close-local-modal');
    closeButton?.click();
  }

  public errorHandling(apiError = false) {
    const shortFormAddressBumper = sessionStorage.getItem('shortFormAddressBumper');
    if (shortFormAddressBumper === "true") {
      this.handleAddressBumperModalClose();
      const shortFormAddressBumper: any = new ResidentialForm();
      shortFormAddressBumper.handleErrorcase(apiError ? 1 : 0);
      return;
    }

    const text1 = document.getElementById("step1-text1");
    const text2 = document.getElementById("step1-text2");
    if (text1) text1.style.display = "none";
    if (text2) text2.style.display = "block";
  
    const step1 = document.querySelector('.step-1');
    const step2 = document.querySelector('.step-2');
    if (step2) step2.classList.add('hidden');
    if (step1) step1.classList.remove('hidden');
  }
  public async handleNextBtnSubmit(event: Event): Promise<void> {
    const formFields = this.getFormFields();
    if (!formFields) {
      this.errorHandling();
      return;
    }

    const conceptCode = (document.getElementById('conceptCode') as HTMLInputElement)?.value;
    const brandData = await getBrandDetailsFromBrandJson(conceptCode);

    // ensure each field is a string (default to empty if not present)
    const address1 = formFields.address1 ?? '';
    const address2 = formFields.address2 ?? '';
    const city = formFields.city ?? '';
    const state = formFields.state ?? '';
    const zipCode = formFields.zipCode ?? '';
    const sAddressParam = `${address1} ${address2} ${city} ${state} ${zipCode}`;
    getFranchiseDetails(sAddressParam) // RoundRobin is true
      .then((result) => {
        if (Array.isArray(result) && result?.length > 0) {
          const jsonItems = result.filter((e: any) => e.franchiseId != null);
          if (jsonItems.length === 1) {
            const shortFormAddressBumper = sessionStorage.getItem('shortFormAddressBumper');
            if (shortFormAddressBumper === "true") {
              sessionStorage.removeItem('shortFormAddressBumper');
              const obj = {
                'franchiseId': result[0].franchiseId,
                'franchiseWebLocationId': result[0].franchiseWebLocationId

              }
              const addressParam = {
                "address": {
                  "state": formFields.state,
                  "city": formFields.city,
                  "postalCode": formFields.zipCode
                }, "title": formFields.address1
              }
              const shortFormAddressBumper: any = new ResidentialForm();
              shortFormAddressBumper.handleSubmit(event, obj, addressParam).finally(() => {
                this.handleAddressBumperModalClose();
              });
            } else {
              const item = jsonItems[0];
              const sRedirectURL = item.locationWebsiteUrl;
              sessionStorage.setItem('alternateConcept', item.alternateConcept);
              localStorage.setItem("franchiseWebLocationId", item.franchiseWebLocationId);
              localStorage.setItem('doingBusinessAs', item.doingBusinessAs);
              sessionStorage.setItem('franchiseeName', item.franchiseeName);
              sessionStorage.setItem('sAddressParam', formFields.zipCode);
              let countryCode = getCountryCode();
              sessionStorage.setItem('countryCode', countryCode);

              if (sRedirectURL) {
                if (sRedirectURL.startsWith('http:')) {
                  window.location.href = sRedirectURL.replace('http:', 'https:');
                } else if (sRedirectURL.startsWith('https:')) {
                  window.location.href = sRedirectURL;
                }
              }
            }
          }
          else {
            this.errorHandling();

          }
        }
        else if(brandData?.lead_contactapi) {
          const addressParam = {
            "address": {
              "state": formFields.state,
              "city": formFields.city,
              "postalCode": formFields.zipCode
            }, "title": formFields.address1
          }
          const shortFormAddressBumper: any = new ResidentialForm();
          shortFormAddressBumper.contactUsApiCall(addressParam);
          this.handleAddressBumperModalClose();
        } else {
          this.errorHandling();
        }
        stopLoader();
      }).catch(() => {
        stopLoader();
        this.errorHandling(true); // api error
        this.handleAddressBumperModalClose();
      })

  }

  private clearValidationErrors(): void {
    document.querySelectorAll('.step-2 .error-msg').forEach(e => e.remove());
    document.querySelectorAll('.invalid-field').forEach(e => e.classList.remove('invalid-field'));
  }

  private handleModalClose(): void {
    this.formElement.reset();
    this.clearValidationErrors();
  }

}